import { useState, useEffect } from 'react';

const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState(null);
  const [country, setCountry] = useState(null);

  // Reverse geocoding to get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      // Use OpenStreetMap Nominatim (free, no API key required)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'VendorList/1.0' // Required by Nominatim
          }
        }
      );
      
      if (!response.ok) {
        return { address: null, country: null };
      }
      
      const data = await response.json();
      if (data.address) {
        const city = data.address.city || data.address.town || data.address.village || data.address.municipality;
        const state = data.address.state || data.address.region || data.address.province;
        const countryName = data.address.country;
        
        // Store country separately - this is critical for filtering
        if (countryName) {
          setCountry(countryName);
          console.log('Country detected from reverse geocoding:', countryName);
        } else {
          console.warn('No country found in reverse geocoding response');
        }
        
        let addressString = null;
        if (city && state) {
          addressString = `${city}, ${state}`;
        } else if (city) {
          addressString = city;
        } else if (state) {
          addressString = state;
        } else if (countryName) {
          addressString = countryName;
        }
        
        return { address: addressString, country: countryName };
      }
      return { address: null, country: null };
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      return { address: null, country: null };
    }
  };

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);
    
    // Don't clear existing location/address/country immediately - only clear if we get a new one
    // This prevents the "data loads then disappears" issue

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const locationData = {
          latitude,
          longitude,
          timestamp: position.timestamp
        };
        
        console.log('Fresh geolocation detected:', { latitude, longitude, timestamp: new Date(position.timestamp) });
        setLocation(locationData);
        
        // Get address and country from coordinates
        const geocodeResult = await reverseGeocode(latitude, longitude);
        if (geocodeResult?.address) {
          console.log('Reverse geocoded address:', geocodeResult.address);
          console.log('Country detected:', geocodeResult.country);
          setAddress(geocodeResult.address);
          // Country is already set in reverseGeocode via setCountry
          if (geocodeResult.country) {
            setCountry(geocodeResult.country);
          }
        } else {
          console.warn('Failed to reverse geocode location');
        }
        
        setLoading(false);
      },
      (err) => {
        let errorMessage = 'Unable to retrieve your location';
        
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case err.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
          default:
            errorMessage = 'An unknown error occurred while retrieving location.';
            break;
        }
        
        console.error('Geolocation error:', errorMessage);
        setError(errorMessage);
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased timeout to 15 seconds for better reliability
        maximumAge: 0 // Always get fresh location, don't use cached (critical for VPN changes)
      }
    );
  };

  useEffect(() => {
    // Optionally auto-detect on mount
    // getCurrentLocation();
  }, []);

  return {
    location,
    address,
    country,
    error,
    loading,
    getCurrentLocation
  };
};

export default useGeolocation;

